import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ndaMathsSet1, ndaMathsSet2, getRandomQuestions } from '../data/ndaMathsQuestions';
import { ndaGATSet1, ndaGATSet2, ndaGATSet3, ndaGATSet4, ndaGATSet5, ndaGATSet6, ndaGATSet7, ndaGATSet8, getRandomGATQuestions } from '../data/ndaGATQuestions';
import { contactInfo, logoUrl } from '../data/mock';
import { 
  Clock, 
  CheckCircle2, 
  XCircle, 
  ArrowRight,
  RotateCcw,
  Trophy,
  Target,
  BookOpen,
  Play,
  Calculator,
  Globe,
  Shuffle,
  Download,
  Mail,
  Phone,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MockExamsPage = () => {
  const [examType, setExamType] = useState('maths');
  const [examState, setExamState] = useState('intro');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(1200);
  const [startTime, setStartTime] = useState(null);
  const [score, setScore] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [selectedSet, setSelectedSet] = useState('random');
  const [questionCount, setQuestionCount] = useState(10);
  const [showLowScoreDialog, setShowLowScoreDialog] = useState(false);
  const [helpFormData, setHelpFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [isSubmittingHelp, setIsSubmittingHelp] = useState(false);
  const resultRef = useRef(null);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateScore = useCallback(() => {
    let correct = 0;
    questions.forEach((q, index) => {
      if (selectedAnswers[index] === q.correctAnswer) {
        correct++;
      }
    });
    return correct;
  }, [selectedAnswers, questions]);

  useEffect(() => {
    let timer;
    if (examState === 'exam' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && examState === 'exam') {
      finishExam();
    }
    return () => clearInterval(timer);
  }, [examState, timeLeft]);

  const getMathsQuestionSet = () => {
    switch (selectedSet) {
      case 'set1':
        return ndaMathsSet1.slice(0, questionCount);
      case 'set2':
        return ndaMathsSet2.slice(0, questionCount);
      case 'random':
      default:
        return getRandomQuestions(questionCount);
    }
  };

  const getGATQuestionSet = () => {
    switch (selectedSet) {
      case 'set1':
        return ndaGATSet1.slice(0, questionCount);
      case 'set2':
        return ndaGATSet2.slice(0, questionCount);
      case 'set3':
        return ndaGATSet3.slice(0, questionCount);
      case 'set4':
        return ndaGATSet4.slice(0, questionCount);
      case 'random':
      default:
        return getRandomGATQuestions(questionCount);
    }
  };

  const startExam = () => {
    const selectedQuestions = examType === 'maths' ? getMathsQuestionSet() : getGATQuestionSet();
    setQuestions(selectedQuestions);
    setExamState('exam');
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setTimeLeft(questionCount * 120);
    setStartTime(Date.now());
    setScore(0);
  };

  const finishExam = async () => {
    const finalScore = calculateScore();
    setScore(finalScore);
    setExamState('result');
    
    const percentage = (finalScore / questions.length) * 100;
    
    // Save result to backend
    try {
      const timeTaken = startTime ? Math.floor((Date.now() - startTime) / 1000) : questionCount * 120;
      await axios.post(`${API}/quiz-results`, {
        exam_type: examType,
        score: finalScore,
        total_questions: questions.length,
        percentage: percentage,
        time_taken: timeTaken,
        answers: selectedAnswers
      });
    } catch (error) {
      console.error('Error saving quiz result:', error);
    }
    
    // Show low score dialog if below 50%
    if (percentage < 50) {
      setTimeout(() => {
        setShowLowScoreDialog(true);
      }, 1500);
    }
  };

  const handleAnswerSelect = (answerIndex) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestion]: answerIndex
    });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = () => {
    finishExam();
  };

  const restartExam = () => {
    setExamState('intro');
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setTimeLeft(1200);
    setScore(0);
    setQuestions([]);
    setSelectedSet('random');
    setStartTime(null);
  };

  const handleHelpFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingHelp(true);
    
    try {
      await axios.post(`${API}/contact`, {
        ...helpFormData,
        source: 'quiz_help',
        quiz_score: (score / questions.length) * 100,
        message: `Student scored ${score}/${questions.length} (${((score / questions.length) * 100).toFixed(1)}%) in ${examType === 'maths' ? 'NDA Mathematics' : 'NDA GAT'} mock exam and needs help with studies.`
      });
      toast.success('Thank you! Our team will contact you soon to help with your preparation.');
      setShowLowScoreDialog(false);
      setHelpFormData({ firstName: '', lastName: '', email: '', phone: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again or contact us directly.');
    } finally {
      setIsSubmittingHelp(false);
    }
  };

  const downloadResults = () => {
    const percentage = ((score / questions.length) * 100).toFixed(1);
    const timeTaken = startTime ? Math.floor((Date.now() - startTime) / 1000) : questionCount * 120;
    
    let resultText = `
================================================================================
                        JAY DEFENCE ACADEMY
                     NDA Mock Examination Results
================================================================================

Exam Type: ${examType === 'maths' ? 'NDA Mathematics' : 'NDA General Ability Test (GAT)'}
Date: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
Time: ${new Date().toLocaleTimeString('en-IN')}

--------------------------------------------------------------------------------
                           SCORE SUMMARY
--------------------------------------------------------------------------------
Total Questions: ${questions.length}
Correct Answers: ${score}
Wrong Answers: ${questions.length - score - Object.values(selectedAnswers).filter(a => a === undefined).length}
Unanswered: ${questions.length - Object.keys(selectedAnswers).length}

FINAL SCORE: ${score}/${questions.length} (${percentage}%)
Time Taken: ${Math.floor(timeTaken / 60)} minutes ${timeTaken % 60} seconds

--------------------------------------------------------------------------------
                         DETAILED ANSWERS
--------------------------------------------------------------------------------
`;

    questions.forEach((q, index) => {
      const isCorrect = selectedAnswers[index] === q.correctAnswer;
      const userAnswer = selectedAnswers[index] !== undefined 
        ? `${String.fromCharCode(65 + selectedAnswers[index])}. ${q.options[selectedAnswers[index]]}`
        : 'Not Answered';
      const correctAnswer = `${String.fromCharCode(65 + q.correctAnswer)}. ${q.options[q.correctAnswer]}`;
      
      resultText += `
Q${index + 1}. [${q.category}] ${isCorrect ? '✓ CORRECT' : '✗ WRONG'}
${q.question}

Your Answer: ${userAnswer}
Correct Answer: ${correctAnswer}
`;
    });

    resultText += `
================================================================================
                        NEED HELP WITH PREPARATION?
================================================================================

Contact Jay Defence Academy:
Email: ${contactInfo.email}
Phone: ${contactInfo.phone}
Address: ${contactInfo.address}

Visit us for expert guidance from decorated veterans!
================================================================================
`;

    const blob = new Blob([resultText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `NDA_${examType === 'maths' ? 'Maths' : 'GAT'}_Results_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Results downloaded successfully!');
  };

  const progress = questions.length > 0 ? ((currentQuestion + 1) / questions.length) * 100 : 0;
  const percentage = questions.length > 0 ? ((score / questions.length) * 100) : 0;

  const mathsTopics = ["Trigonometry", "Probability", "Sequences & Series", "Matrices", "Coordinate Geometry", "Heights & Distances", "Permutations & Combinations", "Applications of Derivatives", "Integration"];
  const gatTopics = ["History", "Geography", "Polity", "Economics", "Physics", "Chemistry", "Biology", "General Science", "Culture"];

  return (
    <Layout>
      {/* Low Score Help Dialog */}
      <Dialog open={showLowScoreDialog} onOpenChange={setShowLowScoreDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-gold-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-gold-600" />
              </div>
              <div>
                <DialogTitle className="text-navy-900">Need Help With Your Studies?</DialogTitle>
                <DialogDescription>
                  You scored {percentage.toFixed(0)}% - Let us help you improve!
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-gray-600 text-sm">
              Our expert faculty can help you improve your scores. Fill in your details and we'll contact you with a personalized study plan.
            </p>
            
            <form onSubmit={handleHelpFormSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="First Name"
                  value={helpFormData.firstName}
                  onChange={(e) => setHelpFormData({...helpFormData, firstName: e.target.value})}
                  required
                />
                <Input
                  placeholder="Last Name"
                  value={helpFormData.lastName}
                  onChange={(e) => setHelpFormData({...helpFormData, lastName: e.target.value})}
                  required
                />
              </div>
              <Input
                type="email"
                placeholder="Email Address"
                value={helpFormData.email}
                onChange={(e) => setHelpFormData({...helpFormData, email: e.target.value})}
                required
              />
              <Input
                type="tel"
                placeholder="Phone Number"
                value={helpFormData.phone}
                onChange={(e) => setHelpFormData({...helpFormData, phone: e.target.value})}
                required
              />
              
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowLowScoreDialog(false)}
                  className="flex-1"
                >
                  Maybe Later
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmittingHelp}
                  className="flex-1 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold"
                >
                  {isSubmittingHelp ? 'Submitting...' : 'Get Help'}
                </Button>
              </div>
            </form>
            
            <div className="text-center pt-2 border-t">
              <p className="text-sm text-gray-500 mb-2">Or contact us directly:</p>
              <div className="flex justify-center gap-4">
                <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="flex items-center gap-1 text-navy-900 hover:text-gold-600 text-sm">
                  <Phone className="w-4 h-4" /> {contactInfo.phone}
                </a>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hero Section */}
      <section className="relative py-16 bg-navy-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800/90 to-olive-900/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img src={logoUrl} alt="Jay Defence Academy" className="h-16 mx-auto mb-6" />
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            NDA Mock <span className="text-gold-400">Tests</span>
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Practice with real NDA-level questions curated by Jay Defence Academy experts.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-cream-50 min-h-[60vh]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Intro State */}
          {examState === 'intro' && (
            <Card className="border-0 shadow-xl overflow-hidden">
              <CardHeader className="bg-navy-900 text-white p-6">
                <CardTitle className="text-2xl font-bold text-center">NDA Practice Test</CardTitle>
              </CardHeader>
              <CardContent className="p-8 bg-white">
                <Tabs value={examType} onValueChange={(v) => { setExamType(v); setSelectedSet('random'); }} className="mb-8">
                  <TabsList className="grid w-full grid-cols-2 bg-cream-100">
                    <TabsTrigger value="maths" className="data-[state=active]:bg-navy-900 data-[state=active]:text-white">
                      <Calculator className="w-4 h-4 mr-2" /> Mathematics
                    </TabsTrigger>
                    <TabsTrigger value="gat" className="data-[state=active]:bg-navy-900 data-[state=active]:text-white">
                      <Globe className="w-4 h-4 mr-2" /> General Ability Test
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="maths" className="mt-6">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-navy-900 mb-2">NDA Mathematics</h3>
                      <p className="text-gray-600 text-sm">40 questions from 2 practice sets</p>
                    </div>
                  </TabsContent>

                  <TabsContent value="gat" className="mt-6">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-navy-900 mb-2">General Ability Test (GAT)</h3>
                      <p className="text-gray-600 text-sm">80 questions from 4 practice sets</p>
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="space-y-3">
                    <Label className="text-navy-900 font-semibold flex items-center gap-2">
                      <Shuffle className="w-4 h-4" /> Question Set
                    </Label>
                    <Select value={selectedSet} onValueChange={setSelectedSet}>
                      <SelectTrigger className="w-full border-gray-200">
                        <SelectValue placeholder="Select question set" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="random">Random Mix</SelectItem>
                        <SelectItem value="set1">Practice Set 1</SelectItem>
                        <SelectItem value="set2">Practice Set 2</SelectItem>
                        {examType === 'gat' && (
                          <>
                            <SelectItem value="set3">Practice Set 3</SelectItem>
                            <SelectItem value="set4">Practice Set 4</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-3">
                    <Label className="text-navy-900 font-semibold flex items-center gap-2">
                      <BookOpen className="w-4 h-4" /> Number of Questions
                    </Label>
                    <Select value={questionCount.toString()} onValueChange={(v) => setQuestionCount(parseInt(v))}>
                      <SelectTrigger className="w-full border-gray-200">
                        <SelectValue placeholder="Select count" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 Questions (Quick)</SelectItem>
                        <SelectItem value="10">10 Questions (Standard)</SelectItem>
                        <SelectItem value="15">15 Questions (Extended)</SelectItem>
                        <SelectItem value="20">20 Questions (Full)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center p-6 bg-cream-50 rounded-xl">
                    <BookOpen className="w-10 h-10 text-navy-900 mx-auto mb-3" />
                    <h3 className="font-bold text-navy-900 mb-1">{questionCount} Questions</h3>
                    <p className="text-gray-600 text-sm">NDA-level problems</p>
                  </div>
                  <div className="text-center p-6 bg-cream-50 rounded-xl">
                    <Clock className="w-10 h-10 text-navy-900 mx-auto mb-3" />
                    <h3 className="font-bold text-navy-900 mb-1">{questionCount * 2} Minutes</h3>
                    <p className="text-gray-600 text-sm">Time limit</p>
                  </div>
                  <div className="text-center p-6 bg-cream-50 rounded-xl">
                    <Target className="w-10 h-10 text-navy-900 mx-auto mb-3" />
                    <h3 className="font-bold text-navy-900 mb-1">Instant Results</h3>
                    <p className="text-gray-600 text-sm">With download option</p>
                  </div>
                </div>

                <div className="mb-8 p-6 bg-olive-50 rounded-xl border border-olive-200">
                  <h4 className="font-semibold text-navy-900 mb-4">Topics Covered:</h4>
                  <div className="flex flex-wrap gap-2">
                    {(examType === 'maths' ? mathsTopics : gatTopics).map((topic, index) => (
                      <Badge key={index} variant="outline" className="border-olive-400 text-olive-700 bg-white">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <Button 
                  onClick={startExam}
                  className="w-full bg-gold-500 hover:bg-gold-400 text-navy-900 font-bold py-6 text-lg"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Start {examType === 'maths' ? 'Mathematics' : 'GAT'} Exam
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Exam State */}
          {examState === 'exam' && questions.length > 0 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between bg-white rounded-xl p-4 shadow-lg">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-navy-900 text-white">Q {currentQuestion + 1} / {questions.length}</Badge>
                  <Badge variant="outline" className="border-olive-500 text-olive-700">{questions[currentQuestion]?.category}</Badge>
                </div>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${timeLeft < 60 ? 'bg-red-100 text-red-700' : 'bg-navy-100 text-navy-900'}`}>
                  <Clock className="w-5 h-5" />
                  <span className="font-mono font-bold text-lg">{formatTime(timeLeft)}</span>
                </div>
              </div>
              
              <Progress value={progress} className="h-2 bg-gray-200" />
              
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-xl font-semibold text-navy-900 mb-8 leading-relaxed">
                    {questions[currentQuestion]?.question}
                  </h2>
                  
                  <RadioGroup 
                    value={selectedAnswers[currentQuestion]?.toString()} 
                    onValueChange={(value) => handleAnswerSelect(parseInt(value))}
                    className="space-y-4"
                  >
                    {questions[currentQuestion]?.options.map((option, index) => (
                      <div 
                        key={index} 
                        className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                          selectedAnswers[currentQuestion] === index 
                            ? 'border-gold-500 bg-gold-50' 
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        onClick={() => handleAnswerSelect(index)}
                      >
                        <RadioGroupItem value={index.toString()} id={`option-${index}`} />
                        <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer text-gray-700">
                          <span className="font-semibold text-navy-900 mr-2">{String.fromCharCode(65 + index)}.</span>
                          {option}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </CardContent>
              </Card>
              
              <div className="flex items-center justify-between">
                <Button onClick={handlePrevious} disabled={currentQuestion === 0} variant="outline" className="border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white">
                  Previous
                </Button>
                
                <div className="hidden md:flex items-center gap-2 flex-wrap max-w-md justify-center">
                  {questions.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentQuestion(index)}
                      className={`w-8 h-8 rounded-lg text-sm font-semibold transition-all ${
                        currentQuestion === index ? 'bg-navy-900 text-white'
                          : selectedAnswers[index] !== undefined ? 'bg-olive-500 text-white'
                          : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
                
                {currentQuestion === questions.length - 1 ? (
                  <Button onClick={handleSubmit} className="bg-gold-500 hover:bg-gold-400 text-navy-900 font-bold">Submit Exam</Button>
                ) : (
                  <Button onClick={handleNext} className="bg-navy-900 hover:bg-navy-800 text-white">
                    Next <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Result State */}
          {examState === 'result' && (
            <Card ref={resultRef} className="border-0 shadow-xl overflow-hidden">
              <CardHeader className={`p-8 text-center ${percentage >= 70 ? 'bg-olive-600' : percentage >= 50 ? 'bg-gold-500' : 'bg-navy-900'}`}>
                <img src={logoUrl} alt="Jay Defence Academy" className="h-12 mx-auto mb-4" />
                <Trophy className={`w-16 h-16 mx-auto mb-4 ${percentage >= 70 ? 'text-white' : percentage >= 50 ? 'text-navy-900' : 'text-gold-400'}`} />
                <CardTitle className={`text-3xl font-bold ${percentage >= 70 ? 'text-white' : percentage >= 50 ? 'text-navy-900' : 'text-white'}`}>
                  {percentage >= 70 ? 'Excellent!' : percentage >= 50 ? 'Good Job!' : 'Keep Practicing!'}
                </CardTitle>
                <p className={`mt-2 ${percentage >= 70 ? 'text-white/80' : percentage >= 50 ? 'text-navy-800' : 'text-gray-300'}`}>
                  {examType === 'maths' ? 'NDA Mathematics' : 'NDA General Ability Test'}
                </p>
              </CardHeader>
              <CardContent className="p-8 bg-white">
                <div className="text-center mb-8">
                  <div className="text-6xl font-bold text-navy-900 mb-2">{score}/{questions.length}</div>
                  <p className="text-gray-600">Questions Answered Correctly</p>
                  <p className="text-lg font-semibold text-olive-600 mt-2">{percentage.toFixed(0)}% Score</p>
                </div>
                
                {/* Download Button */}
                <div className="flex justify-center mb-8">
                  <Button onClick={downloadResults} variant="outline" className="border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white">
                    <Download className="w-5 h-5 mr-2" /> Download Results
                  </Button>
                </div>
                
                {/* Low score message */}
                {percentage < 50 && (
                  <div className="mb-8 p-4 bg-gold-50 border border-gold-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-gold-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-navy-900">Need help improving your score?</h4>
                        <p className="text-gray-600 text-sm mt-1">
                          Our expert faculty can help you prepare better. 
                          <button onClick={() => setShowLowScoreDialog(true)} className="text-gold-600 hover:underline ml-1 font-medium">
                            Click here to get assistance
                          </button>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Answer Review */}
                <div className="space-y-4 mb-8 max-h-96 overflow-y-auto">
                  <h3 className="font-semibold text-navy-900 sticky top-0 bg-white py-2">Answer Review:</h3>
                  {questions.map((q, index) => (
                    <div key={index} className={`p-4 rounded-xl border ${selectedAnswers[index] === q.correctAnswer ? 'bg-olive-50 border-olive-200' : 'bg-red-50 border-red-200'}`}>
                      <div className="flex items-start gap-3">
                        {selectedAnswers[index] === q.correctAnswer ? (
                          <CheckCircle2 className="w-5 h-5 text-olive-600 mt-0.5 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                          <Badge variant="outline" className="mb-2 text-xs">{q.category}</Badge>
                          <p className="font-medium text-gray-800 text-sm mb-2">{q.question}</p>
                          <p className="text-sm text-olive-700">
                            <span className="font-semibold">Correct:</span> {String.fromCharCode(65 + q.correctAnswer)}. {q.options[q.correctAnswer]}
                          </p>
                          {selectedAnswers[index] !== q.correctAnswer && selectedAnswers[index] !== undefined && (
                            <p className="text-sm text-red-600">
                              <span className="font-semibold">Your answer:</span> {String.fromCharCode(65 + selectedAnswers[index])}. {q.options[selectedAnswers[index]]}
                            </p>
                          )}
                          {selectedAnswers[index] === undefined && (
                            <p className="text-sm text-gray-500">Not answered</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <Button onClick={restartExam} className="w-full bg-navy-900 hover:bg-navy-800 text-white font-bold py-6">
                  <RotateCcw className="w-5 h-5 mr-2" /> Try Again
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default MockExamsPage;
